import { apiErrorText } from "../../../api/api-error.js";
import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * `data-screen-label="Agent setup"` — the wizard's loader, and the parts the agent's edit panel shares with it.
 *
 * AMENDED 2026-10-09 (design sync, unit B): the form itself — the step list, one column, Menu / Hours / Orders for a
 * template with an order book, a voice per language, Channels and the Review cards — is `./wizard-form.tsx`, drawn to
 * the Claude Design export of 2026-10-09. This file reads what the form needs and keeps `ContentFields` and
 * `OrderBookPanel`, which the Edit panel of the Agent screen renders (plan-015 INT-B deleted `VoiceChoice`, unused
 * since G1U's voice per language). The notes below describe the round-D form those came from.
 *
 * THE CONTENT STEP IS DRIVEN BY THE TEMPLATE'S `field_set`, NOT BY A BRANCH PER TEMPLATE.
 * F-FAC-1 is explicit: *"adding a template must not change wizard code"*. So this screen renders
 * whatever `agent_template.field_set` says, by **kind** — `upload`, `text`, `book`, `api` — and
 * `./field-format.ts` adds a format to a `text` field it knows (an amount, the opening hours); a
 * field it does not know is a plain required box.
 *
 * REWRITTEN 2026-09-25 — customer-portal review fix round D (`docs/design/fix-round-2026-09-25/D.md`,
 * items #3–#24, #40, #41, #50, #126–#128). What changed, in the order a person meets it:
 *
 *   · ONE NUMBERING (#22): the header, the step list and `?step=` all say 2 to 5; step 1 is the
 *     picker and is listed. A URL for a step ahead of an unfinished one lands on the unfinished one
 *     (#16), and a reload keeps every answer (#15, `./wizard-draft.ts`).
 *   · CONTENT: confirmed orders go to the order book — the only destination that works — and the
 *     person's own order system and payment provider are one "coming soon" line, not a form of
 *     disabled controls (#3, #4, #7, #126). How callers pay offers only what works: cash or card on
 *     delivery, or no payment — plus, beside a hand-over method, online payment (plan-005 OP-38, with
 *     the account's online-payment status and terms shown under it) — and the answer travels to the
 *     build as `payment_method`. The fee and
 *     the threshold are checked as amounts, the opening hours are a week editor (#5). Every field is
 *     required and says so in words on Continue, and the words go as soon as the answer is fixed.
 *   · LANGUAGES AND VOICE: the first language is the account's country's, when that language is
 *     finished (#127). A delivery agent speaks one language for now (the factory's rule). Only voices
 *     that speak every chosen language are offered, by name and the languages they speak (#8–#10,
 *     #13); a chosen voice that no longer fits is replaced, and the screen says so. The greeting box
 *     starts empty with a suggestion from the business's own name, in the greeting language (#11).
 *   · CHANNELS AND NUMBER: the number choice says what happens next (#18, #24); a new number is not
 *     offered while the platform cannot issue one (#40, #50). Web chat is marked soon (#19). The
 *     concurrency choices are the tier's, and the default is one the tier allows (#17); the radios
 *     stay reachable (#20). "Record calls" is asked here, default on (RC-47).
 *   · REVIEW: an agent-name box prefilled from the business, never the prototype's "Roma orders"
 *     (#21, #128), with a warning when another agent already has the name (#41); a summary of every
 *     answer with a way back to its step (#23).
 *
 * THE DOCUMENTS TRAVEL STRAIGHT TO STORAGE (plan-003 RC-32, design §16.8). "Build the agent" creates
 * the VoiceAgent, then — per file — asks for a write-only upload link, PUTs the bytes to it with
 * progress, confirms it, and only then starts the build (`./document-upload.ts`). A failed file says
 * why and the button retries only what is not yet uploaded, without creating the VoiceAgent twice —
 * now across a reload too, because the draft keeps the VoiceAgent's id.
 *
 * AMENDED 2026-10-06 (plan-014 E3b; FR12-ENT-09, FR12-SCR-07): concurrency is the ORGANIZATION's — the field says "Your
 * organization allows N at once, shared across every workspace and agent" (the prototype's line), N being the ceiling
 * the choices are bounded by: the organization's concurrency entitlement from the usage summary (INT 2026-10-07), never
 * the tier's ceiling alone.
 */
import { useQuery } from "@tanstack/react-query";
import { Field, Icon, Input } from "@wetalk/ui";
import type { AgentTemplateField } from "@wetalk/domain";
import { type ReactNode } from "react";

import { GLYPH } from "../../../shell/glyph.js";
import { useGateway } from "../../../api/provider.js";
import {
  agentTemplateQuery,
  forwardFactQuery,
  authSessionQuery,
  dashboardQuery,
  languagePackQuery,
  messagingMediumListQuery,
  numberAvailabilityKeyedQuery,
  voiceAgentListQuery,
  voiceQuery,
  workspaceDetailQuery,
} from "../../../api/query.js";
import type { MessagingMediumRowView } from "../../../api/gateway.js";
import type { LanguageSuggestionView } from "../../../api/slice/wizard.js";
import { SHARED_CONFIGURATION_QUERY_KEY, type SharedVoiceAgentConfigurationView } from "./shared-configuration-gateway.js";
import { descriptionOf, fieldFormatOf, placeholderOf } from "./field-format.js";
import { websiteDescriptionText, websitePlaceholderText } from "./information-input.js";
import { closedWeek, type OpeningWeek } from "./opening-hours.js";
import { OpeningHoursEditor } from "./opening-hours-editor.js";
import { draftKeyOf, type PaymentMethodCode } from "./wizard-draft.js";
import { PAYMENT_CHOICE_METHOD, isAttached, paymentChoiceOf, type WizardContext } from "./wizard-rules.js";
import { onlinePaymentStatusQuery } from "../../payouts/payout-query.js";
import { OnlinePaymentStatusCard } from "../../payouts/online-payment-status.js";
import { WizardForm, WizardUpload } from "./wizard-form.js";
import "../screen.css";
import "./new-agent.css";

/**
 * The four `medium` values of C2a. Voice is always offered; each messaging medium FOLLOWS THE
 * CATALOGUE (plan-004 WC-81, FR-WC-18, review #19) — offered only while `listMessagingMedia` says it is
 * `open`, and marked "soon" while it is waitlisted, unread or unreadable. Nothing here decides it.
 */
const MEDIUM_CHOICE = [
  { code: "voice", get label() { return portalText("operations", "voice_3091c844"); } },
  { code: "web_chat", get label() { return portalText("operations", "web_chat_109d8596"); } },
  { code: "whatsapp", label: "WhatsApp" },
  { code: "viber", get label() { return portalText("operations", "viber_70774829"); } },
] as const;

/** Whether a medium can be chosen: voice always, the others only when the catalogue says `open`. */
export function mediumConnectable(
  code: (typeof MEDIUM_CHOICE)[number]["code"],
  catalogue: readonly MessagingMediumRowView[] | undefined,
): boolean {
  if (code === "voice") return true;
  return catalogue?.find((one) => one.medium === code)?.availability === "open";
}

/** The prototype's `bookPoints`. */
const ORDER_BOOK_POINT = () => ([
  portalText("operations", "caller_details_address_and_notes_on_every_order_28d07b1f"),
  portalText("operations", "printable_kitchen_tickets_and_a_csv_export_16cc463a"),
  portalText("operations", "work_them_from_the_agent_s_orders_page_on_a_phone_or_a_com_8a743e23"),
] as const);

/**
 * How callers pay: only what works today (#126). Each radio is the hand-over part of the list the
 * build receives; online payment (plan-005 OP-38) is its own box beside them, because it is offered
 * IN ADDITION to paying on hand-over — see {@link paymentMethodOf}.
 *
 * AMENDED 2026-10-09 (plan-015 INT-B, G3 AC (c), owner answer 2): the last radio is online payment ALONE — the
 * wizard's "Send a checkout link by SMS" (`PAYMENT_CHOICE_METHOD.link`, `["online"]`), so the Edit panel can say
 * what the wizard can. While the account cannot offer online payment such an agent takes a message instead, and the
 * agent page warns (`online_payment_unavailable`).
 */
const ONLINE_ONLY_KEY = "online";
const PAYMENT_CHOICE: readonly { readonly key: string; readonly label: string; readonly method: readonly PaymentMethodCode[] }[] = [
  { key: "cash", get label() { return portalText("operations", "cash_on_delivery_eda24c69"); }, method: ["cash_on_delivery"] },
  { key: "card", get label() { return portalText("operations", "card_on_delivery_the_driver_brings_a_card_machine_de241fc9"); }, method: ["card_on_delivery"] },
  { key: "both", get label() { return portalText("operations", "cash_or_card_on_delivery_4b459708"); }, method: ["cash_on_delivery", "card_on_delivery"] },
  { key: "none", get label() { return portalText("operations", "no_payment_on_the_call_the_agent_does_not_discuss_it_64230dd3"); }, method: [] },
  { key: ONLINE_ONLY_KEY, get label() { return portalText("operations", "online_payment_only_callers_get_a_checkout_link_by_sms_a6adab35"); }, method: PAYMENT_CHOICE_METHOD.link },
];

/** The hand-over part of an answer — what the radios show. */
function handOverOf(method: readonly PaymentMethodCode[]): readonly PaymentMethodCode[] {
  return method.filter((one) => one !== "online");
}

/** Which radio an answer selects; `online` beside a hand-over method does not change it (it is its own box). */
export function paymentKeyOf(method: readonly PaymentMethodCode[] | null): string | null {
  if (method === null) return null;
  if (paymentChoiceOf(method) === "link") return ONLINE_ONLY_KEY;
  const handOver = handOverOf(method);
  const found = PAYMENT_CHOICE.find(
    (one) => one.key !== ONLINE_ONLY_KEY && one.method.length === handOver.length && one.method.every((each) => handOver.includes(each)),
  );
  return found === undefined ? null : found.key;
}

/** Whether the answer accepts online payment (plan-005 OP-38). */
export function acceptsOnlinePayment(method: readonly PaymentMethodCode[] | null): boolean {
  return method !== null && method.includes("online");
}

/**
 * The list the build receives: the radio's hand-over methods, and `online` beside them when the box
 * is ticked. The BOX rides on a hand-over method: "No payment on the call" clears it. Online payment
 * alone is its own radio (plan-015 INT-B), never the box's doing.
 */
export function paymentMethodOf(handOver: readonly PaymentMethodCode[], online: boolean): readonly PaymentMethodCode[] {
  const base = handOverOf(handOver);
  return online && base.length > 0 ? [...base, "online"] : base;
}

/** "Cash on delivery", or "Cash on delivery, and online payment" — the Review step's line. */
export function paymentSummaryOf(method: readonly PaymentMethodCode[] | null): string {
  const found = PAYMENT_CHOICE.find((one) => one.key === paymentKeyOf(method));
  if (found === undefined) return "—";
  return acceptsOnlinePayment(method) && found.key !== ONLINE_ONLY_KEY ? portalText("operations", "value_and_online_payment_ed416ace", { v0: String(found.label) }) : found.label;
}

export interface WizardScreenProps {
  readonly agentTemplateCode: string;
  /** 2 to 5 — the number the header and the step list show. */
  readonly step: number;
  /** Moves to a step; `replace` when a URL for a later step is sent back to an unfinished one. */
  readonly onStep: (step: number, replace?: boolean) => void;
  /** plan-014 GAP: the shared configuration the new agent starts from (the picker's choice), or `null`. */
  readonly resourceShareId?: string | null;
}

export function WizardScreen({ agentTemplateCode, step, onStep, resourceShareId = null }: WizardScreenProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const listShared = gateway.listSharedVoiceAgentConfigurations;
  const sharedList = useQuery({
    queryKey: SHARED_CONFIGURATION_QUERY_KEY,
    queryFn: (): Promise<readonly SharedVoiceAgentConfigurationView[]> => (listShared === undefined ? Promise.resolve([]) : listShared()),
    enabled: resourceShareId !== null,
    staleTime: 0,
  });

  const catalogue = useQuery(agentTemplateQuery(gateway));
  const languagePack = useQuery(languagePackQuery(gateway));
  const voice = useQuery(voiceQuery(gateway));
  const accountTier = useQuery(dashboardQuery(gateway));
  const numberAvailability = useQuery(numberAvailabilityKeyedQuery(gateway));
  const authSession = useQuery(authSessionQuery(gateway));
  const voiceAgentList = useQuery(voiceAgentListQuery(gateway));
  const mediumCatalogue = useQuery(messagingMediumListQuery(gateway));
  const onlinePayment = useQuery(onlinePaymentStatusQuery(gateway));
  // plan-015 INT-B: whether Forward may be chosen; until it is read (or if it cannot be) the choice stays greyed.
  const forwardFact = useQuery(forwardFactQuery(gateway));
  const workspaceId = authSession.data?.workspaceId ?? "";
  const workspace = useQuery({ ...workspaceDetailQuery(gateway, workspaceId), enabled: workspaceId !== "" });
  const languageSuggestion = useQuery({
    queryKey: ["wizard", "language-suggestion"] as const,
    queryFn: (): Promise<LanguageSuggestionView> => gateway.getLanguageSuggestion(),
    staleTime: 300_000,
  });

  const card = catalogue.data?.find((one) => one.code === agentTemplateCode);

  if (catalogue.data !== undefined && card === undefined) {
    return (
      <section className="c-screen">
        <h1 className="c-screen-title">{portalText("operations", "there_is_no_such_template_c19bae1d")}</h1>
        <p className="c-screen-lede">{portalText("operations", "go_back_to_the_templates_and_choose_one_of_those_offered_1434c135")}</p>
      </section>
    );
  }
  if (card !== undefined && !card.available) {
    return (
      <section className="c-screen">
        <h1 className="c-screen-title">{card.name} {portalText("operations", "is_not_buildable_yet_aba49d76")}</h1>
        <p className="c-screen-lede">{card.dek}</p>
      </section>
    );
  }

  const failed = [catalogue, languagePack, voice, accountTier, numberAvailability, authSession, voiceAgentList, languageSuggestion].find(
    (one) => one.isError,
  );
  if (failed !== undefined) {
    return (
      <p className="c-note c-note-critical">
        {portalText("operations", "the_wizard_could_not_read_what_it_needs_8dfd12fa")}{failed.error instanceof Error ? apiErrorText(failed.error, "") : portalText("operations", "unknown_error_af6321ca")}{portalText("operations", "reload_the_page_to_try_again_your_answers_are_kept_f14a9bef")}</p>
    );
  }

  if (
    card === undefined ||
    languagePack.data === undefined ||
    voice.data === undefined ||
    accountTier.data === undefined ||
    numberAvailability.data === undefined ||
    authSession.data === undefined ||
    voiceAgentList.data === undefined ||
    languageSuggestion.data === undefined
  ) {
    return <p className="c-note">{portalText("operations", "reading_the_catalogue_2a05727e")}</p>;
  }
  if (authSession.data === null) {
    return <p className="c-note">{portalText("operations", "sign_in_to_create_an_agent_1f763fcf")}</p>;
  }

  // plan-014 GAP: a wizard opened from a shared configuration starts from it — once it is read, and only while this
  // workspace may still copy it (accepted, not copied yet, the share standing, the same kind of agent).
  let shared: SharedVoiceAgentConfigurationView | null = null;
  if (resourceShareId !== null) {
    if (sharedList.isError) {
      return (
        <p className="c-note c-note-critical">
          {portalText("operations", "the_shared_configuration_could_not_be_read_5bfbba80")}{sharedList.error instanceof Error ? apiErrorText(sharedList.error, "") : portalText("operations", "unknown_error_af6321ca")}{portalText("operations", "reload_the_page_to_try_again_2318d343")}</p>
      );
    }
    if (sharedList.data === undefined) return <p className="c-note">{portalText("operations", "reading_the_shared_configuration_dd0c821b")}</p>;
    const found = sharedList.data.find((one) => one.resourceShareId === resourceShareId && one.agentTemplateCode === card.code);
    if (found === undefined) {
      return (
        <section className="c-screen">
          <h1 className="c-screen-title">{portalText("operations", "that_shared_configuration_cannot_be_used_fa693776")}</h1>
          <p className="c-screen-lede">
            {portalText("operations", "it_was_revoked_this_workspace_already_made_its_copy_or_it_5052a511")}{card.name} {portalText("operations", "agent_go_back_to_the_templates_and_choose_again_20756cc7")}</p>
        </section>
      );
    }
    shared = found;
  }

  return (
    <WizardForm
      key={`${authSession.data.workspaceId}.${card.code}.${shared === null ? "" : shared.resourceShareId}`}
      card={card}
      step={step}
      onStep={onStep}
      draftKey={shared === null ? draftKeyOf(authSession.data.workspaceId, card.code) : `${draftKeyOf(authSession.data.workspaceId, card.code)}.share.${shared.resourceShareId}`}
      shared={shared}
      languagePack={languagePack.data}
      voice={voice.data}
      ceiling={accountTier.data.organizationConcurrency}
      numberAvailability={numberAvailability.data}
      timeZone={workspace.data?.timeZone ?? null}
      onlinePaymentOffered={onlinePayment.data?.offeredToCallers === true}
      forwardFact={forwardFact.data ?? null}
      webChatConnectable={mediumConnectable("web_chat", mediumCatalogue.data)}
      accountName={authSession.data.accountName}
      voiceAgentName={voiceAgentList.data.map((one) => ({ voiceAgentId: one.voiceAgentId, name: one.displayName }))}
      suggestedLanguage={languageSuggestion.data.languageCode}
    />
  );
}

// ── content ────────────────────────────────────────────────────────────────────────────────

export interface ContentFieldsProps {
  readonly fields: readonly AgentTemplateField[];
  readonly context: WizardContext;
  readonly shown: Readonly<Record<string, string>>;
  readonly onValue: (code: string, value: string) => void;
  readonly onFile: (code: string, file: File) => void;
  readonly onWeek: (code: string, week: OpeningWeek) => void;
  /** Live bug 2026-10-28: puts the kept documents back after typed text replaced one ("Keep … instead"). */
  readonly onKeep: () => void;
}

/** A fee and a threshold share a row; everything else takes its own. */
export function ContentFields({ fields, context, shown, onValue, onFile, onWeek, onKeep }: ContentFieldsProps): ReactNode {
  usePortalTranslation("operations");
  const row: (readonly AgentTemplateField[])[] = [];
  for (const one of fields) {
    const previous = row.at(-1);
    const pairable = (field: AgentTemplateField): boolean => {
      const format = fieldFormatOf(field);
      return format === "amount" || format === "amount_or_none";
    };
    if (pairable(one) && previous !== undefined && previous.length === 1 && pairable(previous[0] as AgentTemplateField)) {
      row[row.length - 1] = [previous[0] as AgentTemplateField, one];
      continue;
    }
    row.push([one]);
  }

  const control = (field: AgentTemplateField): ReactNode => (
    <AgentTemplateFieldControl
      key={field.code}
      field={field}
      value={context.answer.fieldValue[field.code] ?? (field.kind === "choice" ? field.defaultValue : "")}
      picked={context.file[field.code]}
      choiceLocked={context.created !== null && context.created.buildJobId !== ""}
      attached={isAttached(context, field.code)}
      week={context.answer.openingWeek[field.code] ?? closedWeek()}
      error={shown[field.code]}
      onValue={(value) => {
        onValue(field.code, value);
      }}
      onFile={(picked) => {
        onFile(field.code, picked);
      }}
      onWeek={(week) => {
        onWeek(field.code, week);
      }}
      onKeep={onKeep}
    />
  );

  return (
    <>
      {row.map((one) =>
        one.length === 2 ? (
          <div key={(one[0] as AgentTemplateField).code} className="c-setup-pair">
            {one.map(control)}
          </div>
        ) : (
          control(one[0] as AgentTemplateField)
        ),
      )}
    </>
  );
}

export interface OrderBookPanelProps {
  readonly label: string;
  /** The answer so far: `null` until chosen. */
  readonly paymentMethod: readonly PaymentMethodCode[] | null;
  readonly paymentError: string | undefined;
  readonly onPayment: (method: readonly PaymentMethodCode[]) => void;
}

/**
 * Where confirmed orders go — the order book, the destination that works (#3, #126) — and how callers
 * pay. The same panel is the voice agent's Payments setting (the Edit panel), so the online-payment
 * box and the status card below it (plan-005 OP-38) say the same thing in both places: whether
 * online payment does anything yet for this account, what is missing, and the terms.
 */
export function OrderBookPanel({ label, paymentMethod, paymentError, onPayment }: OrderBookPanelProps): ReactNode {
  usePortalTranslation("operations");
  const paymentKey = paymentKeyOf(paymentMethod);
  const onlineOnly = paymentKey === ONLINE_ONLY_KEY;
  const online = acceptsOnlinePayment(paymentMethod);
  const handOver = paymentMethod === null ? [] : handOverOf(paymentMethod);
  // The box is offered once a hand-over method is chosen; online alone is its own radio, so the box is not its.
  const onlineOffered = handOver.length > 0;
  return (
    <Field label={label}>
      {(control) => (
        <div className="c-setup-book" id={control.id} role="group">
          <span className="c-setup-book-title">
            <Icon glyph={GLYPH.archive} size="xl" />
            {portalText("operations", "orders_are_kept_in_your_order_book_0a15770d")}</span>
          <span className="c-setup-book-dek">
            {portalText("operations", "every_confirmed_order_is_filed_on_the_agent_s_orders_page_171f7d6e")}</span>
          <span className="c-setup-book-points">
            {ORDER_BOOK_POINT().map((point) => (
              <span key={point} className="c-setup-book-point">
                <Icon glyph={GLYPH.check} size="md" />
                {point}
              </span>
            ))}
          </span>
          <fieldset
            className="c-setup-payment"
            aria-invalid={paymentError === undefined ? undefined : true}
            aria-describedby={paymentError === undefined ? undefined : `${control.id}-payment-error`}
          >
            <legend className="c-setup-payment-legend">{portalText("operations", "how_callers_pay_084be97a")}</legend>
            {PAYMENT_CHOICE.map((one) => (
              <label key={one.key} className="radio">
                <input
                  type="radio"
                  name="payment-method"
                  value={one.key}
                  checked={paymentKey === one.key}
                  onChange={() => {
                    onPayment(one.key === ONLINE_ONLY_KEY ? one.method : paymentMethodOf(one.method, online && !onlineOnly));
                  }}
                />
                <span className="dot" aria-hidden="true" />
                <span>{one.label}</span>
              </label>
            ))}
            <label className="c-setup-payment-online">
              <input
                type="checkbox"
                checked={online && !onlineOnly}
                disabled={!onlineOffered}
                aria-describedby={`${control.id}-online-dek`}
                onChange={(event) => {
                  const next = event.currentTarget.checked;
                  onPayment(next ? paymentMethodOf(handOver, true) : handOver);
                }}
              />
              <span className="c-setup-payment-online-body">
                <span className="c-setup-payment-online-title">{portalText("operations", "also_take_online_payment_3e90e7f3")}</span>
                <span id={`${control.id}-online-dek`} className="c-setup-payment-online-dek">
                  {onlineOffered
                    ? portalText("operations", "callers_get_a_payment_link_by_text_message_or_in_the_websi_15c52242")
                    : onlineOnly
                      ? portalText("operations", "online_payment_is_how_callers_pay_while_your_account_canno_cc41ea06")
                      : portalText("operations", "choose_cash_or_card_on_delivery_first_callers_need_a_way_t_d6556656")}
                </span>
              </span>
            </label>
            <OnlinePaymentStatusCard linkToPayouts />
            {paymentError === undefined ? null : (
              <span id={`${control.id}-payment-error`} className="wt-field-error" role="alert">
                {paymentError}
              </span>
            )}
          </fieldset>
        </div>
      )}
    </Field>
  );
}

interface AgentTemplateFieldControlProps {
  readonly choiceLocked: boolean;
  readonly picked: File | undefined;
  readonly field: AgentTemplateField;
  readonly value: string;
  readonly attached: boolean;
  readonly week: OpeningWeek;
  readonly error: string | undefined;
  readonly onValue: (value: string) => void;
  /** An `upload` field's picked file (RC-32). */
  readonly onFile: (file: File) => void;
  readonly onWeek: (week: OpeningWeek) => void;
  readonly onKeep: () => void;
}

/** One `agent_template.field_set` row, rendered by kind and, for a `text` field, by format. */
function AgentTemplateFieldControl(props: AgentTemplateFieldControlProps): ReactNode {
  usePortalTranslation("operations");
  const { field, value, error } = props;
  const errorProp = error === undefined ? {} : { error };

  if (field.kind === "upload") {
    // Live bug 2026-10-28: the wizard's own upload control, so typed text replaces a kept document only when confirmed.
    return (
      <WizardUpload
        field={field}
        label={field.label}
        value={value}
        picked={props.picked}
        attached={props.attached}
        error={error}
        preview={null}
        onFile={props.onFile}
        onKeep={props.onKeep}
      />
    );
  }
  if (field.kind === "choice") {
    return (
      <Field label={field.label} {...errorProp} {...(props.choiceLocked ? { description: portalText("operations", "use_start_over_to_change_this_choice_after_a_build_has_sta_ea479c58") } : {})}>
        {(control) => (
          <div id={control.id} role="radiogroup" aria-label={field.label} aria-describedby={control["aria-describedby"]} className="c-setup-chips">
            {field.options.map((option) => (
              <label key={option.value} className="c-setup-note">
                <input type="radio" name={field.code} disabled={props.choiceLocked} value={option.value} checked={value === option.value}
                  onChange={() => { props.onValue(option.value); }} /> {option.label}
              </label>
            ))}
          </div>
        )}
      </Field>
    );
  }
  if (field.kind === "website") {
    // plan-018 WAPP-A: the website address, as the wizard asks it (the Edit panel shares this control).
    return (
      <Field label={field.label} description={websiteDescriptionText()} {...errorProp}>
        {(control) => (
          <Input
            {...control}
            type="url"
            inputMode="url"
            autoComplete="url"
            spellCheck={false}
            name={field.code}
            maxLength={2048}
            placeholder={websitePlaceholderText()}
            value={value}
            onChange={(event) => {
              props.onValue(event.currentTarget.value);
            }}
          />
        )}
      </Field>
    );
  }
  if (field.kind !== "text") return null;

  const format = fieldFormatOf(field);
  if (format === "opening_hours") {
    return (
      <Field
        label={field.label}
        description={portalText("operations", "the_days_and_times_the_agent_takes_orders_in_your_workspac_fd6d7cdc")}
        {...errorProp}
      >
        {(control) => (
          <OpeningHoursEditor
            id={control.id}
            week={props.week}
            describedBy={control["aria-describedby"]}
            invalid={control["aria-invalid"]}
            onWeek={props.onWeek}
          />
        )}
      </Field>
    );
  }

  return (
    <Field label={field.label} {...(format === null ? {} : { description: descriptionOf(format) })} {...errorProp}>
      {(control) => (
        <Input
          {...control}
          name={field.code}
          maxLength={field.maxLength}
          {...(format === null ? {} : { placeholder: placeholderOf(format), inputMode: "decimal" as const })}
          value={value}
          onChange={(event) => {
            props.onValue(event.currentTarget.value);
          }}
        />
      )}
    </Field>
  );
}

